import React from "react";
import { Tag, Space, Button, Tooltip, Divider } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import DataTable from "../../../components/DataTable";
import { useTheme } from "../../../services/useTheme";
import { MODULE_LOGOS, MODULE_GROUPS } from "../../../constants/modules";

const getRoleDisplay = (roles) => {
  if (!roles || roles.length === 0) return { label: "N/A", color: "default" };
  const n = roles[0].name;
  if (n === "ROLE_ADMIN") return { label: "Admin", color: "#474747" };
  if (n === "ROLE_MANAGER") return { label: "Manager", color: "#cf8516" };
  if (n === "ROLE_USER") return { label: "User", color: "#0d2e4d" };
  return { label: n.replace("ROLE_", ""), color: "default" };
};

export default function UsersTable({
  data,
  loading,
  page,
  pageSize,
  onPageChange,
  openUpdateDialog,
  openDeleteDialog,
  openPrivilegesDialog,
}) {
  const isDark = useTheme();
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "5%",
      sorter: (a, b) =>
        (a.id || "").toString().localeCompare((b.id || "").toString()),
    },
    {
      title: "User Name",
      dataIndex: "username",
      key: "username",
      width: "15%",
      sorter: (a, b) => (a.username || "").localeCompare(b.username || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: "15%",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Role",
      dataIndex: "roles",
      key: "role",
      width: "10%",
      render: (r) => {
        const { label, color } = getRoleDisplay(r);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Modules",
      dataIndex: "activatedModules",
      key: "modules",
      width: "45%",
      render: (modules) => {
        if (!modules?.length) return "No Modules";
        const labels = [...new Set(modules.map((m) => m.label))].filter(
          (l) => !["HDFS", "Hue", "Jupyter"].includes(l),
        );

        const sections = Object.values(MODULE_GROUPS)
          .map((g, i) => {
            const f = labels.filter((l) => g.includes(l));
            return f.length ? (
              <React.Fragment key={i}>
                <Space wrap size={[8, 8]} className="modules-group">
                  {f.map((l) => (
                    <Tooltip key={l} title={l}>
                      <img
                        className="table-module-logo"
                        src={MODULE_LOGOS(isDark)[l]}
                        alt={l}
                        style={{
                          width: 36,
                          height: 36,
                          objectFit: "contain",
                          borderRadius: 4,
                        }}
                      />
                    </Tooltip>
                  ))}
                </Space>
              </React.Fragment>
            ) : null;
          })
          .filter(Boolean);
        return (
          <Space
            className="modules-cell"
            split={
              <Divider
                type="vertical"
                style={{ height: 40, borderColor: "#bfbfbf", borderWidth: 2 }}
              />
            }
            size="middle"
          >
            {sections}
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, r) => {
        const isAdmin = r.roles?.some((role) => role.name === "ROLE_ADMIN");
        return (
          <Space size="small">
            {!isAdmin && (
              <Button
                type="text"
                icon={<AppstoreOutlined />}
                onClick={() => openPrivilegesDialog(r)}
                style={{ color: "#D97A07" }}
                title="Manage Modules"
              />
            )}
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openUpdateDialog(r)}
              style={{ color: "#D97A07" }}
              title="Edit User"
            />
            {!isAdmin && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => openDeleteDialog(r)}
                title="Delete User"
              />
            )}
          </Space>
        );
      },
    },
  ];
  return (
    <DataTable
      rowKey="id"
      columns={columns}
      data={data}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      pageSizeOptions={[10, 20, 50, 100]}
    />
  );
}
